import { Link } from "react-router-dom";
import { CalendarDays, BookOpen, Quote, Flame, ArrowRight } from "lucide-react";
import { SCRIPTURES, SAINT_QUOTES } from "./data";
import { useLocalDate, localDayOfYear, localDateKey } from "@/hooks/useLocalDate";
import { useTodayCalendar, NO_MARIAN_CELEBRATION } from "@/components/calendar/useTodayCalendar";
import { Reveal, SectionHeading } from "./Reveal";
import { TRADITIONAL_ENGLISH, BALTIMORE_SOURCE } from "@/data/traditionalRosary";
import { PrayerAudio } from "@/components/prayers/PrayerAudio";

export default function TodayWithMary() {
  const now = useLocalDate();
  const { feast, loading, error } = useTodayCalendar(now);
  const dayOfYear = localDayOfYear(now);
  const scripture = SCRIPTURES[dayOfYear % SCRIPTURES.length];
  const quote = SAINT_QUOTES[dayOfYear % SAINT_QUOTES.length];
  const dateLabel = now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const cards = [
    {
      icon: CalendarDays,
      testid: "today-feast",
      label: "General Roman Calendar",
      title: loading ? "Looking up today" : error ? "Calendar unavailable" : feast ? feast.name : "Walking with Mary",
      body: loading ? "Loading today’s observances…" : error || (feast ? `${feast.rank} · ${feast.note}` : NO_MARIAN_CELEBRATION),
      to: "/calendar",
      cta: "Open the calendar",
    },
    { icon: BookOpen, testid: "today-scripture", label: "Scripture · RSV-CE", title: scripture.ref, body: scripture.text, source: scripture.source, url: scripture.url, serif: true },
    {
      icon: Flame,
      testid: "today-prayer",
      label: "A short prayer",
      title: "Hail Mary",
      body: TRADITIONAL_ENGLISH.hail_mary, source: "Baltimore Catechism no. 2 · Angelical Salutation", url: BALTIMORE_SOURCE,
      serif: true,
    },
    { icon: Quote, testid: "today-quote", label: quote.kind === "paraphrase" ? "A reflection on the saints" : "From the saints", title: quote.author, body: quote.text, source: quote.source, url: quote.url, serif: true },
  ];

  return (
    <section id="today" data-testid="today-with-mary" className="bg-paleblue/50 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="Today with Mary" title="One day, one grace">
            A feast, a verse, a prayer, and a word from the saints — renewed each morning.
          </SectionHeading>
          <Reveal delay={0.1}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slateblue" data-testid="today-date" data-local-date={localDateKey(now)} aria-live="polite">
              {dateLabel}
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card, i) => (
            <Reveal key={card.testid} delay={i * 0.08} className="h-full">
              <div data-testid={card.testid} className="card-lift flex h-full flex-col rounded-2xl border border-marian/15 bg-white/90 p-7 backdrop-blur">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-paleblue text-marian">
                  <card.icon className="h-5 w-5" strokeWidth={1.7} aria-hidden="true" />
                </span>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">{card.label}</p>
                <h3 className="mt-2 font-serif text-xl font-semibold text-midnight sm:text-2xl">{card.title}</h3>
                {card.testid === "today-prayer" && <PrayerAudio prayerId="hail-mary" prefix="today-prayer" lang="en" />}
                <p data-testid={`${card.testid}-text`} className={`mt-3 flex-1 text-sm leading-relaxed text-charcoal/85 ${card.serif ? "font-serif text-base italic" : ""}`}>
                  {card.body}
                </p>
                {card.source && <a href={card.url} target="_blank" rel="noreferrer" data-testid={`${card.testid}-source`} className="mt-4 text-xs leading-relaxed text-marian underline underline-offset-4 transition-colors hover:text-midnight">{card.source}</a>}
                {card.to && (
                  <Link
                    to={card.to}
                    data-testid="today-calendar-link"
                    className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-marian transition-colors hover:text-midnight"
                  >
                    {card.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
